import { useCallback, type FC, type ReactNode } from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import { DatePicker } from '@mui/x-date-pickers';
import { Trans, useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { TextField, MenuButton } from '../../../../../components/base';
import { DownloadExcelTemplate } from '../../../../../components/ControlPanel/components/DownloadExcelTemplate';
import { ImportExcelButton } from '../../../../../components/ControlPanel/components/ImportExcelButton';
import { FullScreenButton } from '../../../../../components/FullScreen';
import useDrawer from '../../../../../hooks/useDrawer';
import { AddEntityControl, ButtonGridColumns, ControlPanelWrapper } from '../../../../../modules/EntitiesGrid';
import { TaskFilters } from '../../../../../modules/Tasks/components/TaskFilters';
import { dailyTasksFiltersState, newEntityReady } from '../../../../../modules/Tasks/state/dailyTasks';
import { useDailyTaskNewRow, useTabs } from '../../../../../modules/Tasks/state/dailyTasks/hooks';
import { Drawer } from '../../../../../state/drawer';
import { permissionState } from '../../../../../state/event';
import { useExportToPdfTableDailyTasks } from '../../../../../state/export';
import { userEventSettings } from '../../../../../state/user';
import { useToggleCompactMode } from '../../../../event-settings';

type Props = {
  onExportToExcel: () => void;
  onImportFromExcel: (file: File) => void;
  loadingImportFromExcel?: boolean;
  fontSizeControl?: ReactNode;
};

export const ControlPanel: FC<Props> = ({
  onExportToExcel,
  onImportFromExcel,
  loadingImportFromExcel,
  fontSizeControl,
}) => {
  const { t } = useTranslation();
  const { currentTab, dayOptions, hasTasks, handleChangeTab, disabled } = useTabs();
  const { showDrawer } = useDrawer();
  const {
    loading: loadingNewRow,
    newEntity,
    prepareNewEntity: prepareNewTask,
    destroyNewEntity,
    addNewEntity,
  } = useDailyTaskNewRow();
  const { canCreateOperationalTask } = useRecoilValue(permissionState);
  const { loading: loadingExportPdfTable, execute: exportToPdfTable } = useExportToPdfTableDailyTasks();
  const userEventSetting = useRecoilValue(userEventSettings);
  const { execute: toggleCompactMode, loading: loadingToggleCompactMode } = useToggleCompactMode('drs');

  const exportToPdfTableCallback = useCallback(() => exportToPdfTable(t), [exportToPdfTable, t]);
  const loading = loadingImportFromExcel || loadingExportPdfTable || loadingToggleCompactMode;

  return (
    <ControlPanelWrapper
      leftSlot={
        <>
          {!hasTasks && currentTab && (
            <DatePicker
              label={t('day-daily-task')}
              inputFormat='dd/MM/yyyy'
              disabled={disabled}
              value={'day' in currentTab && currentTab.day ? currentTab.day : Date.now()}
              onChange={handleChangeTab}
              renderInput={(params) => <TextField required {...params} />}
            />
          )}
          {dayOptions && (
            <AddEntityControl
              disabled={!canCreateOperationalTask}
              loading={loadingNewRow}
              newEntity={newEntity}
              destroyNewEntity={destroyNewEntity}
              addNewEntity={addNewEntity}
              newEntityReady={newEntityReady}
              addNewButtons={[
                {
                  text: t('add-new-daily-task-inline'),
                  action: () => prepareNewTask(dayOptions),
                },
              ]}
            />
          )}
        </>
      }
      rightSlot={
        <>
          <TaskFilters
            filtersState={dailyTasksFiltersState}
            onClickFilterButton={() => showDrawer({ type: Drawer.DAILY_TASKS_FILTER })}
          />
          <ButtonGridColumns
            openGridColumnsSettings={() => showDrawer({ type: Drawer.DAILY_TASKS_COLUMNS })}
            columnsText={t('columns')}
          />
          {fontSizeControl}
          <FullScreenButton />
          <MenuButton
            buttonContent={<MenuIcon />}
            isLoading={loading}
            commands={[
              {
                content: (
                  <Trans
                    i18nKey={
                      userEventSetting.drsGridSettings.viewMode === 'compact'
                        ? 'table-compact-disable'
                        : 'table-compact-enable'
                    }
                  />
                ),
                onClick: toggleCompactMode,
              },
              ...(canCreateOperationalTask
                ? [
                    {
                      content: <ImportExcelButton onClick={onImportFromExcel} />,
                      disableAutoClose: true,
                    },
                  ]
                : []),
              ...(canCreateOperationalTask
                ? [
                    {
                      content: <DownloadExcelTemplate link='/excelTemplates/dailyPlanTemplate.xlsx' />,
                    },
                  ]
                : []),
              ...(onExportToExcel
                ? [
                    {
                      content: <Trans i18nKey='export-to-excel' />,
                      onClick: onExportToExcel,
                    },
                  ]
                : []),
              {
                content: <Trans i18nKey='export-table' />,
                onClick: exportToPdfTableCallback,
              },
            ]}
          />
        </>
      }
    />
  );
};
